import React, { useEffect, useState, ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Reusable confirm dialog that captures a mandatory (or optional) reason note.
// Used for admin deactivate / delete / restore (decision #12 — reason required).
interface JustificationDialogProps {
  open: boolean;
  title: string;
  message?: string;
  extra?: ReactNode; // e.g. deletion-impact summary
  requireReason?: boolean;
  confirmText?: string;
  confirmColor?: "error" | "primary" | "success";
  busy?: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

const JustificationDialog: React.FC<JustificationDialogProps> = ({
  open,
  title,
  message,
  extra,
  requireReason = true,
  confirmText = "Confirm",
  confirmColor = "error",
  busy = false,
  onConfirm,
  onClose,
}) => {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const disabled = busy || (requireReason && reason.trim().length === 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800, display: "flex", justifyContent: "space-between" }}>
        {title}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {message && (
          <DialogContentText sx={{ color: "text.secondary", mb: extra ? 1.5 : 2 }}>
            {message}
          </DialogContentText>
        )}
        {extra && <Box sx={{ mb: 2 }}>{extra}</Box>}
        <TextField
          label={requireReason ? "Reason (required)" : "Reason (optional)"}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          autoFocus
          required={requireReason}
          error={requireReason && reason.trim().length === 0}
          helperText={
            requireReason && reason.trim().length === 0
              ? "A reason is required and will be saved to the audit log."
              : " "
          }
        />
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, justifyContent: "center" }}>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={confirmColor}
          onClick={() => onConfirm(reason.trim())}
          disabled={disabled}
          sx={{ textTransform: "none" }}
        >
          {busy ? "Please wait…" : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JustificationDialog;
